/* eslint-disable no-param-reassign */
/* eslint-disable @typescript-eslint/no-magic-numbers */

import { GetQuestionsResponse, GetStatsResponse } from 'hooks/api/useCueingEngine';
import { produce } from 'immer';
import { CueingEngineResult } from 'pages/Journey/hooks/useStageCueingEngine';
import { AttemptInfo, EomModule, EomModuleStats, QuestionType } from 'pages/TaskPlayer/types';
import { getValidatedDataForCueingEngine } from 'pages/TaskPlayer/validationUtil';
import { GetModuleListResponse } from 'services/api';
import { v4 as uuidv4 } from 'uuid';
import { StateCreator } from 'zustand';
import { AppState } from 'slices/appSlice';
import { TestState } from 'slices/testSlice';

const MAX_QUESTIONS_ALLOWED = 3;
const MIN_QUESTIONS_FOR_FAIL = 3;
const PASS_THRESHOLD = 2 / 3;
const getScoreRange = (questionStats: QuestionStats[], availableQuestions: QuestionType[]) => {
  const totalAsked = questionStats.length;
  const remainingCount = Math.min(MAX_QUESTIONS_ALLOWED - totalAsked, availableQuestions.length);
  const correctCount = questionStats.filter((stat) => stat.isCorrect).length;
  return {
    minScore: correctCount / (totalAsked + remainingCount),
    maxScore: (correctCount + remainingCount) / (totalAsked + remainingCount),
    score: correctCount / totalAsked,
    remainingCount,
  };
};

const selectRandomQuestion = (questions: QuestionType[]) => {
  return questions[Math.floor(Math.random() * questions.length)];
};

const isCurrentSetOver = (questionStats: QuestionStats[], availableQuestions: QuestionType[]) => {
  const { minScore, maxScore, remainingCount, score } = getScoreRange(
    questionStats,
    availableQuestions,
  );
  return {
    currentSetOver: remainingCount === 0 || minScore >= PASS_THRESHOLD || maxScore < PASS_THRESHOLD,
    score,
  };
};

const precompute = (
  getQuestionsResponse: GetQuestionsResponse,
  getStatsResponse: GetStatsResponse,
) => {
  return getValidatedDataForCueingEngine(getQuestionsResponse, getStatsResponse);
};

interface BasicQuestionStats {
  questionId: string;
  format: string;
  medium: string;
  seqNo: number;
  isCorrect: boolean;
  translationViewed: string[];
  type: number;
  cueCount: number;
}
interface QuestionStats extends BasicQuestionStats, AttemptInfo {
  attempt1?: AttemptInfo; // without any cues
  attempt2?: AttemptInfo; // for cue 1
  attempt3?: AttemptInfo; // for cue 2
}

export interface StageState {
  /** api response related state */
  moduleListResponse: GetModuleListResponse | undefined;
  getStatsResponse: GetStatsResponse | undefined;
  getQuestionsResponse: GetQuestionsResponse | undefined;
  difficultyWiseQuestions: { [difficulty: number]: QuestionType[] };
  minDifficulty: number;
  maxDifficulty: number;
  eomModuleMapping: {
    [evidenceId: string]: {
      [language: string]: EomModule;
    };
  };
  totalSets: number;
  errors: string[]; // shared
  warnings: string[]; // shared

  /** stats related state */
  eomModuleStats: EomModuleStats[]; // shared
  translationViewed: string[]; // shared
  overallQuestionStats: QuestionStats[];

  /** stage journey related state */
  showStageTask: boolean;

  /** api execution related state */
  addStatsRetryCount: number;
  submitResultRetryCount: number;

  /** logic related state */
  currentModuleIndex: number | undefined; // shared
  question?: QuestionType; // shared
  isEvaluatingPivot: boolean;
  stageTaskResult: { status: 'PASS' | 'FAIL'; lastDifficulty: number } | undefined;
  currentSet: number;
  currentQuestionStat?: QuestionStats;
  currentDifficultyQuestionStats: QuestionStats[];

  /** api response parsing related behaviour */
  initialiseModules: ({
    moduleListResponse,
  }: {
    moduleListResponse: GetModuleListResponse;
  }) => void;
  initialiseCueing: ({
    getStatsResponse,
    getQuestionsResponse,
  }: {
    getStatsResponse: GetStatsResponse;
    getQuestionsResponse: GetQuestionsResponse;
  }) => void;

  /** stats related behaviour */
  storeUserAttempt: ({
    attemptInfo,
    hintUsed,
    isCorrect,
    medium,
  }: {
    attemptInfo: AttemptInfo;
    isCorrect: boolean;
    hintUsed: number;
    medium: string;
  }) => void;
  addTranslationViewed: (language: string) => void; // shared
  setEomModuleStats: (newStat: EomModuleStats) => void; // shared

  /** stage journey related behaviour */
  setShowStageTask: (show: boolean) => void;
  clearStageState: () => void;
  clearStageTaskResult: () => void;
  clearSharedState: () => void; // shared

  /** api execution related behaviour */
  incrementAddStatsRetryCount: () => void;
  incrementSubmitResultRetryCount: () => void;
  getSubmitResultRetryCount: () => number;
  getAddStatsRetryCount: () => number;

  /** logic related behaviour */
  setCurrentModuleIndex: (currentModuleIndex: number) => void;
  processUserAnswer: () => void;
  getCurrentDifficulty: () => number | undefined;
  getAvailableQuestions: () => QuestionType[];
  getCueingEngineResult: () => CueingEngineResult | undefined;
}

const getSharedInitialState = () => {
  return {
    errors: [], // shared
    warnings: [], // shared
    eomModuleStats: [], // shared
    translationViewed: [], // shared
    currentModuleIndex: undefined, // shared
    question: undefined, // shared
  };
};

const getInitialState = () => {
  return {
    ...getSharedInitialState(),
    /** api response related state */
    moduleListResponse: undefined,
    getStatsResponse: undefined,
    getQuestionsResponse: undefined,
    difficultyWiseQuestions: {},
    minDifficulty: 1,
    maxDifficulty: 3,
    eomModuleMapping: {},
    totalSets: 1,

    /** stats related state */
    overallQuestionStats: [],

    /** stage journey related state */
    showStageTask: false,

    /** api execution related state */
    addStatsRetryCount: 0,
    submitResultRetryCount: 0,

    /** logic related state */
    isEvaluatingPivot: false,
    stageTaskResult: undefined,
    currentSet: 0,
    currentQuestionStat: undefined,
    currentDifficultyQuestionStats: [],
  };
};

const getInititalTranslationViewed = (
  currentQuestion: QuestionType | undefined,
  mediumPriority: string[],
) => {
  if (currentQuestion && mediumPriority) {
    // eslint-disable-next-line unicorn/prefer-array-find
    const availableLanguages = mediumPriority.filter(
      (medium) => currentQuestion.mediumMapping[medium],
    );
    return [availableLanguages[0]];
  }
  return [];
};

export const createStageSlice: StateCreator<
  TestState & StageState & AppState,
  [['zustand/devtools', never]],
  [],
  StageState
> = (set, get) => ({
  ...getInitialState(),

  /** api response parsing related behaviour */
  initialiseModules: ({ moduleListResponse }) => {
    const { moduleList, lastCompletedModuleId } = moduleListResponse;
    const lastCompletedModuleIndex = lastCompletedModuleId
      ? moduleList.findIndex((module) => module.id === lastCompletedModuleId)
      : -1;
    if (lastCompletedModuleIndex < moduleList.length - 1) {
      set(
        {
          moduleListResponse,
          currentModuleIndex: lastCompletedModuleIndex + 1,
        },
        false,
        'stageSlice/initialiseModules',
      );
    } else {
      set(
        {
          showStageTask: true,
        },
        false,
        'stageSlice/initialiseModules',
      );
    }
  },
  initialiseCueing: ({
    getStatsResponse,
    getQuestionsResponse,
  }: {
    getStatsResponse: GetStatsResponse;
    getQuestionsResponse: GetQuestionsResponse;
  }) =>
    set(
      (_state) =>
        produce(_state, (state: StageState) => {
          const {
            difficultyWiseQuestions,
            minDifficulty,
            maxDifficulty,
            formativeQuestions,
            pivotQuestions,
            isValid,
            errors,
            totalSets,
          } = precompute(getQuestionsResponse, getStatsResponse);
          if (isValid) {
            state.warnings = errors;
            state.getQuestionsResponse = {
              ...getQuestionsResponse,
              formativeQuestions,
              pivotQuestions,
            };
            state.getStatsResponse = getStatsResponse;
            state.eomModuleMapping = getQuestionsResponse.eomModuleMapping;
            state.totalSets = totalSets;
            state.currentSet = 0;
            state.difficultyWiseQuestions = difficultyWiseQuestions;
            state.maxDifficulty = maxDifficulty;
            state.minDifficulty = minDifficulty || 1;
            const unaidedAccuracy =
              (getStatsResponse.unaidedAccuracyMetrics.unaidedCorrectAnswers || 0) /
              (getStatsResponse.unaidedAccuracyMetrics.totalFormativeQuestions || 1);
            if (unaidedAccuracy >= 0.8 && getStatsResponse.studentStageDetails.roundNumber === 1) {
              state.isEvaluatingPivot = true;
              state.question = selectRandomQuestion(pivotQuestions);
            } else {
              const startingDifficulty =
                state.minDifficulty ||
                (getStatsResponse.studentStageDetails.lastDifficulty || 0) + 1;
              state.isEvaluatingPivot = false;
              state.question = selectRandomQuestion(difficultyWiseQuestions[startingDifficulty]);
            }
            state.translationViewed = getInititalTranslationViewed(
              state.question,
              getQuestionsResponse.mediumPriority,
            );
            state.overallQuestionStats = [];
            state.currentDifficultyQuestionStats = [];
          } else {
            state.errors = errors;
          }
        }),
      false,
      'stageSlice/initialiseCueing',
    ),

  /** stats related behaviour */
  storeUserAttempt: ({
    attemptInfo,
    hintUsed,
    isCorrect,
    medium,
  }: {
    attemptInfo: AttemptInfo;
    isCorrect: boolean;
    hintUsed: number;
    medium: string;
  }) =>
    set(
      (_state) =>
        produce(_state, (state: StageState) => {
          if (!state.currentDifficultyQuestionStats) {
            state.currentDifficultyQuestionStats = [];
          }
          if (!state.currentQuestionStat) {
            state.currentQuestionStat = {} as QuestionStats;
          }
          const {
            currentDifficultyQuestionStats,
            currentQuestionStat,
            question,
            overallQuestionStats,
            translationViewed,
            isEvaluatingPivot,
          } = state;
          const { answer, startTime, endTime, duration } = attemptInfo;
          if (!question) {
            return;
          }
          currentQuestionStat.answer = answer;
          currentQuestionStat.medium = medium;
          currentQuestionStat.isCorrect = isCorrect;
          currentQuestionStat.duration = (currentQuestionStat.duration || 0) + duration;
          currentQuestionStat.endTime = endTime;
          const attemptKey = `attempt${hintUsed + 1}` as 'attempt1' | 'attempt2' | 'attempt3';
          if (!isEvaluatingPivot) {
            currentQuestionStat.cueCount = hintUsed;
            currentQuestionStat[attemptKey] = attemptInfo;
          }
          if (hintUsed === 0) {
            currentQuestionStat.questionId = question.id;
            currentQuestionStat.type = question.type;
            currentQuestionStat.format = question.format;
            currentQuestionStat.startTime = startTime;
            currentQuestionStat.seqNo =
              overallQuestionStats.length + currentDifficultyQuestionStats.length + 1;
            currentQuestionStat.translationViewed = translationViewed;
            currentDifficultyQuestionStats.push(currentQuestionStat);
          } else {
            const lastIndex = currentDifficultyQuestionStats.length - 1;
            if (lastIndex >= 0) {
              currentDifficultyQuestionStats[lastIndex] = currentQuestionStat;
            }
          }
        }),
      false,
      'stageSlice/storeUserAttempt',
    ),
  addTranslationViewed: (newLanguage: string) =>
    set(
      (state) => ({
        translationViewed: [...new Set([...state.translationViewed, newLanguage])],
      }),
      false,
      'stageSlice/addTranslationViewed',
    ),
  setEomModuleStats: (newStats: EomModuleStats[] | EomModuleStats) =>
    set(
      (state) => {
        if (Array.isArray(newStats)) {
          return { eomModuleStats: newStats };
        }

        const moduleKey = `${newStats.questionId}_${newStats.eomModuleId}`;
        const existingIndex = state.eomModuleStats.findIndex(
          (stat) => `${stat.questionId}_${stat.eomModuleId}` === moduleKey,
        );

        if (existingIndex >= 0) {
          const updatedStats = [...state.eomModuleStats];
          updatedStats[existingIndex] = newStats;
          return { eomModuleStats: updatedStats };
        }

        return { eomModuleStats: [...state.eomModuleStats, newStats] };
      },
      false,
      'stageSlice/setEomModuleStats',
    ),

  /** stage journey related behaviour */
  setShowStageTask: (showStageTask: boolean) =>
    set({ showStageTask }, false, 'stageSlice/setShowStageTask'),
  clearStageState: () => set({ ...getInitialState() }, false, 'stageSlice/clearStageState'),

  clearStageTaskResult: () =>
    set({ stageTaskResult: undefined }, false, 'stageSlice/clearStageTaskResult'),
  clearSharedState: () => {
    set({ ...getSharedInitialState() }, false, 'stageSlice/clearSharedState');
  },

  /** api execution related behaviour */
  incrementAddStatsRetryCount: () =>
    set(
      (state) => ({
        addStatsRetryCount: state.addStatsRetryCount + 1,
      }),
      false,
      'stageSlice/incrementAddStatsRetryCount',
    ),
  incrementSubmitResultRetryCount: () =>
    set(
      (state) => ({
        submitResultRetryCount: state.submitResultRetryCount + 1,
      }),
      false,
      'stageSlice/incrementSubmitResultRetryCount',
    ),
  getSubmitResultRetryCount: () => get().submitResultRetryCount,
  getAddStatsRetryCount: () => get().addStatsRetryCount,

  setCurrentModuleIndex: (currentModuleIndex: number) =>
    set({ currentModuleIndex }, false, 'stageSlice/setCurrentModuleIndex'),

  processUserAnswer: () =>
    set(
      (_state) =>
        produce(_state, (state: StageState) => {
          const {
            overallQuestionStats,
            currentDifficultyQuestionStats,
            isEvaluatingPivot,
            difficultyWiseQuestions,
            getCurrentDifficulty,
            maxDifficulty,
            getAvailableQuestions,
            minDifficulty,
            getQuestionsResponse,
            stageTaskResult,
          } = state;
          if (stageTaskResult) return;
          if (!getQuestionsResponse) return;
          const { pivotQuestions } = getQuestionsResponse;
          const currentDifficulty = getCurrentDifficulty();
          const availableQuestions = getAvailableQuestions();

          const { currentSetOver, score } = isCurrentSetOver(
            currentDifficultyQuestionStats,
            availableQuestions,
          );

          if (currentSetOver) {
            state.overallQuestionStats = [
              ...overallQuestionStats,
              ...currentDifficultyQuestionStats,
            ];
            state.currentSet += 1;
            state.currentDifficultyQuestionStats = [];
            if (isEvaluatingPivot) {
              state.stageTaskResult = {
                status:
                  score >= PASS_THRESHOLD || availableQuestions.length < MIN_QUESTIONS_FOR_FAIL
                    ? 'PASS'
                    : 'FAIL',
                lastDifficulty: maxDifficulty,
              };
              state.question = undefined;
            } else if (
              score < PASS_THRESHOLD &&
              currentDifficulty &&
              difficultyWiseQuestions[currentDifficulty].length >= MIN_QUESTIONS_FOR_FAIL
            ) {
              state.stageTaskResult = {
                status: 'FAIL',
                lastDifficulty:
                  currentDifficulty && currentDifficulty > minDifficulty
                    ? currentDifficulty - 1
                    : 0,
              };
              state.question = undefined;
            } else {
              const continueFormative =
                currentDifficulty &&
                currentDifficulty < maxDifficulty &&
                difficultyWiseQuestions[currentDifficulty + 1];
              state.isEvaluatingPivot = !continueFormative;
              state.question = selectRandomQuestion(
                continueFormative ? difficultyWiseQuestions[currentDifficulty + 1] : pivotQuestions,
              );
            }
          } else {
            state.question = selectRandomQuestion(availableQuestions);
          }
          state.translationViewed = getInititalTranslationViewed(
            // eslint-disable-next-line unicorn/consistent-destructuring
            state.question,
            // eslint-disable-next-line unicorn/consistent-destructuring
            getQuestionsResponse?.mediumPriority,
          );
          state.currentQuestionStat = undefined;
        }),
      false,
      'stageSlice/processUserAnswer',
    ),
  getCurrentDifficulty: () => get().question?.difficulty,
  getAvailableQuestions: () => {
    const {
      getQuestionsResponse,
      difficultyWiseQuestions,
      getCurrentDifficulty,
      isEvaluatingPivot,
    } = get();
    if (getQuestionsResponse) {
      const { pivotQuestions } = getQuestionsResponse;
      const currentDifficulty = getCurrentDifficulty();
      if (currentDifficulty) {
        const questions = isEvaluatingPivot
          ? pivotQuestions
          : difficultyWiseQuestions[currentDifficulty];
        const attemptedQuestionIds = new Set(
          [...get().currentDifficultyQuestionStats, ...get().overallQuestionStats].map(
            (stat) => stat.questionId,
          ),
        );
        return questions?.filter((q) => !attemptedQuestionIds.has(q.id)) || [];
      }
    }
    return [];
  },
  getCueingEngineResult: () => {
    const {
      selectedSubject,
      currentStage,
      getStatsResponse,
      overallQuestionStats,
      eomModuleStats,
      stageTaskResult,
      missionAttemptNo,
    } = get();
    let finalResult;
    if (getStatsResponse) {
      const { stageAttemptNo, engagementStrategyId: strategyId } =
        getStatsResponse.studentStageDetails;
      if (currentStage && selectedSubject && stageTaskResult && missionAttemptNo) {
        const { status, lastDifficulty } = stageTaskResult;
        finalResult = {
          stageId: currentStage.id,
          status,
          subjectId: selectedSubject.id,
          stageAttemptNo,
          missionAttemptNo,
          strategyId,
          lastDifficulty,
          eomModuleStats,
          questionStats: { attemptId: uuidv4(), stats: overallQuestionStats },
        };
      }
    }
    return finalResult;
  },
});
